import React from 'react';
import { connect } from 'react-redux';
import { useInView } from 'react-intersection-observer';
import { useTranslator } from 'tradera-lang/translate';
import {
    Breakpoint,
    BREAKPOINTS
} from 'tradera-components/breakpoint/breakpoint';
import uiActions from 'tradera-state/ui/actions';
import FooterLocalizationLink from './footer-localization-link';
import initData from 'init-data';
import { isSwedishOrUndefined } from 'tradera-utils/languagecode';
import AppstoreLogo from '@tradera/blueprint/theme/logos/appstore.svg?jsx';
import FacebookIcon from '@tradera/blueprint/theme/icons2/network-fb.svg?jsx';
import GooglePlayLogo from '@tradera/blueprint/theme/logos/googleplay.svg?jsx';
import InstagramIcon from '@tradera/blueprint/theme/icons2/network-ig.svg?jsx';
import LinkedInIcon from '@tradera/blueprint/theme/icons2/network-linkedin.svg?jsx';
import PinterestIcon from '@tradera/blueprint/theme/icons2/network-pin.svg?jsx';
import TwitterIcon from '@tradera/blueprint/theme/icons2/network-twitter.svg?jsx';
import BankidIcon from '@tradera/blueprint/theme/icons2/bankid.svg?jsx';
import TraderaSymbol from '@tradera/blueprint/theme/logos/tradera_symbol.svg?jsx';

import {
    BuyingLinks,
    SellingLinks,
    ContactAndHelpLinks,
    CouponCodeLinks
} from './footer-links';
import { FooterPartners } from './footer-partners';

import './site-footer.scss';

const SiteFooter = props => {
    const { pageName, isCouponationNavLinkEnabled, openGdprSettings } = props;
    const { t } = useTranslator();

    const [symbolRef, inView] = useInView({
        threshold: 0
    });

    return (
        <div className="site-footer position-relative bg-branded text-styled mt-5 mt-md-0">
            <div className="site-footer__width site-width w-100 d-flex flex-column pt-4">
                <div className="site-footer__wrapper">
                    <div className="row px-2 px-md-3 px-lg-2">
                        {/* Köp */}
                        <BuyingLinks />

                        {/* Sälj */}
                        <SellingLinks />
                        {/* Kontakt & hjälp */}
                        <ContactAndHelpLinks
                            pageName={pageName}
                            openGdprSettings={openGdprSettings}
                        />
                        {/* Rabattkoder */}
                        <CouponCodeLinks
                            isCouponationNavLinkEnabled={
                                isCouponationNavLinkEnabled
                            }
                        />
                        <FooterPartners />
                    </div>
                    <div className="row mx-2 mx-md-3 mx-lg-2">
                        <div className="site-footer__icon-row mb-2">
                            <div className="row flex-column flex-md-row py-2">
                                {/* Social media */}
                                <div className="d-flex col-12 col-md-auto justify-content-center justify-content-md-start">
                                    <a
                                        aria-label="Tradera Facebook"
                                        href="https://www.facebook.com/Tradera"
                                        className="mr-3"
                                        data-ga-category="Footer"
                                        data-ga-action="Navigation"
                                        data-ga-label="Sociala medier - Facebook"
                                        rel="nofollow">
                                        <FacebookIcon className="icon icon-md icon-dark" />
                                    </a>
                                    <a
                                        aria-label="Tradera Twitter"
                                        href="https://twitter.com/tradera"
                                        className="mr-3"
                                        data-ga-category="Footer"
                                        data-ga-action="Navigation"
                                        data-ga-label="Sociala medier - Twitter"
                                        rel="nofollow">
                                        <TwitterIcon className="icon icon-md icon-dark" />
                                    </a>
                                    <a
                                        aria-label="Tradera Pintrest"
                                        href="https://www.pinterest.se/tradera/"
                                        className="mr-3"
                                        data-ga-category="Footer"
                                        data-ga-action="Navigation"
                                        data-ga-label="Sociala medier - Pinterest"
                                        rel="nofollow">
                                        <PinterestIcon className="icon icon-md icon-dark" />
                                    </a>
                                    <a
                                        aria-label="Tradera Instagram"
                                        href="https://www.instagram.com/tradera/"
                                        className="mr-3"
                                        data-ga-category="Footer"
                                        data-ga-action="Navigation"
                                        data-ga-label="Sociala medier - Instagram"
                                        rel="nofollow">
                                        <InstagramIcon className="icon icon-md icon-dark" />
                                    </a>
                                    <a
                                        aria-label="Tradera LinkedIn"
                                        href="https://www.linkedin.com/company/tradera/"
                                        className="mr-3"
                                        data-ga-category="Footer"
                                        data-ga-action="Navigation"
                                        data-ga-label="Sociala medier - LinkedIn"
                                        rel="nofollow">
                                        <LinkedInIcon className="icon icon-md icon-dark" />
                                    </a>
                                    <a
                                        aria-label="Tradera BankId"
                                        href="https://www.bankid.com/"
                                        target="_blank"
                                        className="mr-0"
                                        data-ga-category="Footer"
                                        data-ga-action="Navigation"
                                        data-ga-label="BankId"
                                        rel="nofollow noopener noreferrer">
                                        <BankidIcon className="icon icon-md icon-dark" />
                                    </a>
                                </div>
                                <span className="border-left border-dark mx-4 d-none d-md-inline" />
                                {/* App download */}
                                <div className="d-flex col-12 col-md-auto mt-3 mt-md-0 justify-content-center justify-content-md-start">
                                    <a
                                        href="https://itunes.apple.com/se/app/tradera-k%C3%B6p-och-s%C3%A4lj/id427984084?mt=8"
                                        rel="nofollow"
                                        className="mr-2"
                                        data-ga-category="Footer"
                                        data-ga-action="Navigation"
                                        data-ga-label="App Store">
                                        <AppstoreLogo
                                            height="20"
                                            title={t(
                                                'footer_downloadFromAppStore',
                                                {
                                                    appName: 'App Store'
                                                }
                                            )}
                                            className="icon-dark"
                                        />
                                    </a>
                                    <a
                                        href="https://play.google.com/store/apps/details?id=com.tradera&hl=sv"
                                        rel="nofollow"
                                        className="ml-2"
                                        data-ga-category="Footer"
                                        data-ga-action="Navigation"
                                        data-ga-label="App Store">
                                        <GooglePlayLogo
                                            height="20"
                                            title={t(
                                                'footer_downloadFromAppStore',
                                                {
                                                    appName: 'Google Play'
                                                }
                                            )}
                                            className="icon-dark"
                                        />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row px-2 px-md-3 px-lg-2">
                        <div className="col col-md-8 col-lg-12 d-flex flex-wrap position-relative">
                            <Breakpoint downTo={BREAKPOINTS.MD}>
                                <FooterLocalizationLink />
                            </Breakpoint>
                            <a
                                data-ga-category="Footer"
                                className="mr-2 mb-1"
                                data-ga-action="Navigation"
                                data-ga-label="Användaravtal"
                                href={
                                    isSwedishOrUndefined(
                                        initData.languageCodeIso2
                                    )
                                        ? 'https://info.tradera.com/anvandaravtal/'
                                        : 'https://info.tradera.com/en/anvandaravtal-eng/'
                                }>
                                {t('footer_userAgreement')}
                            </a>
                            <a
                                data-ga-category="Footer"
                                className="mr-2 mb-1"
                                data-ga-action="Navigation"
                                data-ga-label="Integritetspolicy"
                                href={
                                    isSwedishOrUndefined(
                                        initData.languageCodeIso2
                                    )
                                        ? 'https://info.tradera.com/sakerhetscenter/integritetspolicy/'
                                        : 'https://info.tradera.com/en/integritetespolicy-eng/'
                                }>
                                {t('footer_integrityPolicy')}
                            </a>
                            <a
                                data-ga-category="Footer"
                                className="mr-2 mb-1"
                                data-ga-action="Navigation"
                                data-ga-label="Cookies"
                                href="https://info.tradera.com/cookies-webb-beacons-och-liknande-teknik/">
                                {t('footer_cookies')}
                            </a>
                            <a
                                data-ga-category="Footer"
                                className="mr-2 mb-1"
                                data-ga-action="Navigation"
                                data-ga-label="Om oss"
                                href="/info/om-oss">
                                {t('footer_aboutTradera')}
                            </a>
                            <a
                                data-ga-category="Footer"
                                className="mr-2 mb-1"
                                data-ga-action="Navigation"
                                data-ga-label="Jobb"
                                href="https://careers.tradera.com/">
                                {t('footer_availableJobs')}
                            </a>
                        </div>
                    </div>
                    {/* Copyright */}
                    <div className="row px-2 px-md-3 px-lg-2">
                        <p className="col-12 mb-5 text-no-wrap">
                            © 1999&ndash;{new Date().getFullYear()} Tradera
                            Sweden AB
                        </p>
                    </div>
                </div>
            </div>
            <div className="site-footer__symbol" ref={symbolRef}>
                <TraderaSymbol
                    className={`site-footer__symbol-logo ${
                        inView ? 'play-animation' : ''
                    }`}
                />
            </div>
        </div>
    );
};

const mapStateToProps = state => ({
    pageName: state.page.name,
    isCouponationNavLinkEnabled:
        state.environment.featureSwitches['couponation-nav-link']
});

const mapDispatchToProps = dispatch => ({
    openGdprSettings: () => dispatch(uiActions.openGdprSettings())
});

export default connect(mapStateToProps, mapDispatchToProps)(SiteFooter);
